
import Vue from 'vue';
import BaseWelcomeStepPage from './BaseWelcomeStepPage.vue';
import Select, { SelectOption } from '~/components/builders/Base/Select.vue';

import CandidateService from '~/services/CandidateService';
import { WorkPreference } from '~/services/CandidateService';
import { commonProps, setupBuilder } from '~/utils/candidate';
import { CandidateJobBuilder } from '~/utils/candidate/CandidateJobBuilder';
import { JobNationality } from '~/utils/candidate/CandidateJobBuilder.types';

import countries from 'highered-utils/data/countries.json';
import xor from 'lodash/xor';

export default Vue.extend({
    name: 'WelcomeStep1Page',
    components: {
        BaseWelcomeStepPage,
        Select,
    },
    props: {
        pageNumber: { type: Number, required: true },
        pageCount: { type: Number, required: true },
        clickBack: {
            type: Function,
            default: () => {},
        },
        clickNext: {
            type: Function,
            default: () => {},
        },
        error: {
            type: Function,
            default: () => {},
        },

        ...commonProps(),
    },
    data() {
        return {
            builder: null as CandidateJobBuilder | null,
            service: new CandidateService(),

            nationalities: [] as JobNationality[],
            country: '',
            jobPreferences: [] as WorkPreference[],
            job: false,

            isLoading: false,
        };
    },
    methods: {
        handleAddNationality() {
            this.nationalities.push({
                id: CandidateJobBuilder.generateId(),
                nationality: '',
            });
        },
        handleChangeNationality(idx: number, nationality: string) {
            const natio = this.nationalities[idx];
            const natios = [...this.nationalities];
            natios[idx] = {
                ...natio,
                nationality,
            };
            this.nationalities = natios;
        },
        handleRemoveNationality(idx: number) {
            const natios = [...this.nationalities];
            natios.splice(idx, 1);
            this.nationalities = natios;
        },

        isJobPreferencesChecked(value: WorkPreference): boolean {
            return this.jobPreferences.includes(value);
        },
        handleJobChecked() {
            this.job = !this.job;
            if (!this.job) this.jobPreferences = [];
        },
        handleJobPreferenceChecked(value: WorkPreference) {
            this.jobPreferences = xor(this.jobPreferences, [value]);
        },

        handleClickBack() {
            this.$emit('clickBack');
        },
        handleClickNext() {
            if (!this.builder || this.builder.isEmpty()) {
                this.$emit('clickNext');
                return;
            }

            const request = this.builder.buildRequest();

            this.isLoading = true;
            this.service
                .updateCandidateProfile(request)
                .then(() => {
                    this.isLoading = false;
                    this.$emit('clickNext');
                })
                .catch(err => {
                    this.$emit('error', err);
                })
                .finally(() => {
                    this.isLoading = false;
                });

            this.builder.reset();
        },
    },
    computed: {
        optionsCountries(): SelectOption[] {
            return countries.map(c => ({
                value: c.value,
                label: `${c.prefix} ${c.label}`,
                search: c.label,
            }));
        },
        jobOptions(): SelectOption<WorkPreference>[] {
            const t = (id: string) =>
                this.$t(`profile.personalDetails.jobOptions.${id}`).toString();

            return [
                { value: 'internship', label: t('internship') },
                { value: 'graduate_program', label: t('graduate_program') },
                { value: 'part_time', label: t('part_time') },
                { value: 'full_time', label: t('full_time') },
                { value: 'volunteer', label: t('volunteer') },
            ];
        },
        isFormValid(): boolean {
            return Boolean(this.country && this.nationalities.length);
        },
    },
    watch: {},
    mounted() {
        this.builder = setupBuilder.apply(this);

        this.nationalities = this.builder.getNationalities();
        this.country = this.builder.getCandidateField('country') || '';
        this.jobPreferences = this.builder.getPreferencesField('workPreferences') || [];
        this.job = !!this.jobPreferences.length;

        if (this.nationalities.length === 0) this.handleAddNationality();

        this.$watch('nationalities', this.builder.watchNationalities());
        this.$watch('country', this.builder.watchCandidate('country'));
        this.$watch('jobPreferences', this.builder.watchPreference('workPreferences'));
    },
});
